import { ActionTargetReference } from '../types/actionTypes';

export type PendingActionLaunchType =
  | 'REBOOT'
  | 'UPDATE_CONFIG'
  | 'UPDATE_DEPLOY'
  | 'UPDATE_LICENSE'
  | 'RTU_INFO'
  | 'RTU_DEBUG_LOG'
  | 'DOWNLOAD_CONFIG'
  | 'DOWNLOAD_DEPLOY';

export interface PendingActionLaunchRecord {
  actionType: PendingActionLaunchType;
  createdAtUtc: string;
  fallbackName: string;
  idempotencyKey: string;
  requestFingerprint: string;
  targets: ActionTargetReference[];
}

const STORAGE_KEY = 'dms.actions.pending-launches.v3';
const SERIAL_ONLY_STORAGE_KEY = 'dms.actions.pending-launches.v2';
const LEGACY_STORAGE_KEY = 'dms.actions.pending-launches.v1';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const canonicalActionTargets = (targets: ActionTargetReference[]): ActionTargetReference[] =>
  Array.from(
    new Map(
      targets
        .map((target) => ({ rtuId: Number(target.rtuId), serialNo: String(target.serialNo ?? '').trim() }))
        .filter((target) => Number.isInteger(target.rtuId) && target.rtuId > 0 && Boolean(target.serialNo))
        .map((target) => [JSON.stringify([target.rtuId, target.serialNo]), target] as const),
    ).values(),
  ).sort((left, right) => left.rtuId - right.rtuId || left.serialNo.localeCompare(right.serialNo));

const stableSerialize = (value: unknown): string => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'undefined';
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== undefined)
    .sort(([left], [right]) => left.localeCompare(right));
  return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`).join(',')}}`;
};

/**
 * This compact fingerprint is only a client-side recovery discriminator.
 * Backend idempotency uses its own SHA-256 fingerprint and remains authoritative.
 */
export const createPendingActionRequestFingerprint = (value: unknown): string => {
  const serialized = stableSerialize(value);
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < serialized.length; index += 1) {
    const code = serialized.charCodeAt(index);
    first = Math.imul(first ^ code, 0x01000193);
    second = Math.imul(second ^ code, 0x85ebca6b);
  }
  return `${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}-${serialized.length}`;
};

const isUsableRecord = (value: unknown): value is PendingActionLaunchRecord => {
  const record = value as Partial<PendingActionLaunchRecord> | null;
  if (!record || !record.idempotencyKey || !record.actionType || !record.createdAtUtc
      || !record.fallbackName || !record.requestFingerprint) {
    return false;
  }
  if (!Array.isArray(record.targets) || canonicalActionTargets(record.targets).length === 0) {
    return false;
  }
  const createdAt = Date.parse(record.createdAtUtc);
  return Number.isFinite(createdAt) && Date.now() - createdAt <= MAX_AGE_MS;
};

export const listPendingActionLaunches = (): PendingActionLaunchRecord[] => {
  if (typeof window === 'undefined') return [];
  try {
    // Serial-only records cannot prove the selected RTU identity. They are
    // intentionally discarded instead of being replayed against a reused ID.
    window.sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    window.sessionStorage.removeItem(SERIAL_ONLY_STORAGE_KEY);
    const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? '[]');
    const records = Array.isArray(parsed)
      ? parsed.filter(isUsableRecord).map((record) => ({
          ...record,
          targets: canonicalActionTargets(record.targets),
        }))
      : [];
    if (records.length !== (Array.isArray(parsed) ? parsed.length : 0)) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    }
    return records;
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return [];
  }
};

export const savePendingActionLaunch = (record: PendingActionLaunchRecord): void => {
  if (typeof window === 'undefined') return;
  const normalized = {
    ...record,
    targets: canonicalActionTargets(record.targets),
  };
  const records = listPendingActionLaunches().filter(
    (current) => current.idempotencyKey !== normalized.idempotencyKey,
  );
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...records, normalized]));
};

export const removePendingActionLaunch = (idempotencyKey: string): void => {
  if (typeof window === 'undefined') return;
  const records = listPendingActionLaunches().filter(
    (record) => record.idempotencyKey !== idempotencyKey,
  );
  if (records.length === 0) {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(records));
};

export const clearPendingActionLaunches = (): void => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    window.sessionStorage.removeItem(SERIAL_ONLY_STORAGE_KEY);
  }
};

export const findPendingActionLaunch = (
  actionType: PendingActionLaunchType,
  targets: ActionTargetReference[],
  requestFingerprint: string,
): PendingActionLaunchRecord | undefined => {
  const targetKey = JSON.stringify(canonicalActionTargets(targets));
  return listPendingActionLaunches().find(
    (record) => record.actionType === actionType
      && record.requestFingerprint === requestFingerprint
      && JSON.stringify(record.targets) === targetKey,
  );
};
